import { confettiTrackerAtom } from "@mirage/mosaics/UserProfilePage/atoms";
import { useWindowSize } from "@react-hookz/web";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import ReactDOM from "react-dom";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawAnniversary(this: any, ctx: CanvasRenderingContext2D) {
  // `this` is a Particle object from react-confetti
  // `Particle` is not exported, so we can't use it in the type
  const confettiType = this.confettiType ?? Math.floor(Math.random() * 4);
  const w = this.w ?? 12;
  const h = this.h ?? 12;
  this.confettiType = confettiType;
  this.w = w;
  this.h = h;

  switch (confettiType) {
    case 0:
      return drawCircle(ctx, w / 2);
    case 1:
      return drawRect(ctx, w, h);
    case 2:
      return drawStrip(ctx, w, h);
    case 3:
      return drawLogo(ctx);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawBirthday(this: any, ctx: CanvasRenderingContext2D) {
  // `this` is a Particle object from react-confetti
  // `Particle` is not exported, so we can't use it in the type
  const confettiType = this.confettiType ?? Math.floor(Math.random() * 3);
  const w = this.w ?? 12;
  const h = this.h ?? 12;
  this.confettiType = confettiType;
  this.w = w;
  this.h = h;

  switch (confettiType) {
    case 0:
      return drawCircle(ctx, w / 2);
    case 1:
      return drawRect(ctx, w, h);
    case 2:
      return drawStrip(ctx, w, h);
  }
}

function drawLogo(ctx: CanvasRenderingContext2D) {
  ctx.save();
  const scale = 0.65;
  ctx.scale(scale, scale);
  ctx.translate(-21.9462 / scale, -15.0002 / scale);
  ctx.fill(
    new Path2D(
      `M21.9462 15.0002L11.8902 21.1817L21.9462 27.3632L11.8902 33.5446L21.9462 39.7261L32.0022 33.5446L42.0583 39.7261L52.1143 33.5446L42.0583 27.3632L52.1143 21.267L42.0583 15.0002L32.0022 21.1817L21.9462 15.0002ZM32.0022 21.1817L42.0583 27.3632L32.0022 33.5446L21.9462 27.3632L32.0022 21.1817Z`,
    ),
  );
  ctx.fill(
    new Path2D(
      `M21.9462 42.8188L32.0022 36.6373L37.0303 39.7281L42.0583 42.8188L32.0022 49.0002L21.9462 42.8188Z`,
    ),
  );
  ctx.restore();
}

function drawCircle(ctx: CanvasRenderingContext2D, radius: number) {
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, 2 * Math.PI);
  ctx.fill();
}

function drawRect(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillRect(-w / 2, -h / 2, w, h);
}

function drawStrip(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillRect(-w / 6, -h / 2, w / 3, h);
}

export const UserProfileConfetti = ({
  userId,
  type,
}: {
  userId: string;
  type: "birthday" | "anniversary";
}) => {
  const windowSize = useWindowSize();
  const [confettiTracker, setConfettiTracker] = useAtom(confettiTrackerAtom);
  const [isRecycling, setIsRecycling] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  useEffect(() => {
    setIsDisabled(true);
    setIsRecycling(true);
    const timerId = setTimeout(() => {
      setIsRecycling(false);
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [userId]);

  useEffect(() => {
    if (!confettiTracker[userId]) {
      setIsDisabled(false);
      setConfettiTracker((prev) => ({ ...prev, [userId]: true }));
    }
  }, [userId, confettiTracker, setConfettiTracker]);

  if (isDisabled) {
    return null;
  }

  return ReactDOM.createPortal(
    <Confetti
      width={windowSize.width}
      height={windowSize.height}
      recycle={isRecycling}
      opacity={0.8}
      numberOfPieces={150}
      gravity={0.25}
      confettiSource={{ x: windowSize.width * 0.5, y: -100, w: 0, h: 0 }}
      initialVelocityX={{
        min: -windowSize.width * 0.005,
        max: windowSize.width * 0.005,
      }}
      initialVelocityY={{ min: 0, max: 0 }}
      drawShape={type === "anniversary" ? drawAnniversary : drawBirthday}
      {...(type === "anniversary"
        ? { colors: ["#1A1918", "#0061FE", "#ffffff"] }
        : {})}
    />,
    document.body,
  );
};
